import { useEffect, useState } from "react"
import {
  parseIfString,
  fetchStorefront,
  normalizeHandleDash,
} from "@app/scripts/lib/utils"

type ColourSwatchEntry = {
  colour?: {
    value?: string
  }
  image?: {
    reference?: {
      image?: {
        url?: string
      }
    }
  }
}

type GraphQLSwatchResponse = {
  [handle: string]: ColourSwatchEntry | null
}

type NormalizedSwatch = {
  colour_handle: string
  colour: string | null
  image: string | null
  colourName: string
}

// Key for storing/getting colour swatches in session storage
const COLOUR_SWATCH_KEY = "global_colour_swatches"

// Builds a GraphQL query to fetch colour swatches by their handles
function buildColourSwatchQuery(handles: string[]) {
  return `
    query {
      ${handles
        .map((handle) => {
          // Some colour handles start with a number, which is invalid for GraphQL aliases.
          // This logic ensures we prefix an underscore if the handle starts with a number
          // to prevent the query from breaking, even though handles are always strings.
          let sanitizedAlias = handle.replace(/[^a-zA-Z0-9_]/g, "_")
          if (/^[0-9]/.test(sanitizedAlias)) {
            sanitizedAlias = `_${sanitizedAlias}`
          }

          return `
            ${sanitizedAlias}: metaobject(handle: { handle: "${handle}", type: "colour_swatches" }) {
              colour: field(key: "colour") {
                value
              }
              image: field(key: "image") {
                value
                reference {
                  __typename
                  ... on MediaImage {
                    image {
                      url
                    }
                  }
                }
              }
            }
          `
        })
        .join("\n")}
    }
  `
}

// Fetches colour swatches from the storefront by their handles
async function fetchColourSwatchesByHandles(
  handles: string[]
): Promise<NormalizedSwatch[]> {
  if (!handles.length) return []

  const query = buildColourSwatchQuery(handles)
  const data: GraphQLSwatchResponse = await fetchStorefront(query)

  const swatches: NormalizedSwatch[] = []

  Object.entries(data).forEach(([handle, entry]) => {
    if (!entry) {
      console.warn(`No swatch data for handle: ${handle}`)
      return
    }

    swatches.push({
      colour_handle: normalizeHandleDash(handle),
      colour: entry.colour?.value ?? null,
      image: entry.image?.reference?.image?.url ?? null,
    })
  })

  return swatches
}

// Extracts unique colour handles from the results against the `mfield_dotapparel21_siblings` field
function extractRequiredColourHandles(results: []): string[] {
  const handles = new Set<string>()

  results.forEach((result) => {
    const swatches =
      parseIfString(
        result?.mfield_dotapparel21_siblings ||
          result?.attributes?.mfield_dotapparel21_siblings
      ) || []

    swatches.forEach((swatch: NormalizedSwatch) => {
      const handle = normalizeHandleDash(swatch?.colourName)
      if (handle) handles.add(handle)
    })
  })

  return Array.from(handles)
}

// Retrieves cached swatches from session storage
function getCachedSwatches(): NormalizedSwatch[] {
  try {
    const rawColourSwatchJSON = sessionStorage.getItem(COLOUR_SWATCH_KEY)
    const parsedColourSwatchData = JSON.parse(rawColourSwatchJSON || "[]")
    return Array.isArray(parsedColourSwatchData) ? parsedColourSwatchData : []
  } catch (err) {
    console.warn("Invalid swatch cache format", err)
    return []
  }
}

// Custom hook to manage colour swatch cache
export function useSwatchCache(results: []) {
  const [swatchColours, setSwatchColours] = useState<NormalizedSwatch[]>([])

  useEffect(() => {
    if (!results?.length) return

    const syncSwatchCache = async () => {
      const requiredColours = extractRequiredColourHandles(results)
      const cachedSwatches = getCachedSwatches()

      const cachedHandles = cachedSwatches
        .map((colour) => colour.colour_handle?.toLowerCase?.())
        .filter(Boolean)

      const missingColours = requiredColours.filter(
        (colour) => !cachedHandles.includes(colour)
      )

      if (missingColours.length === 0) {
        setSwatchColours(cachedSwatches)
        return
      }

      const normalizedMissingColours = missingColours
        .filter(Boolean)
        .map((handle) => normalizeHandleDash(String(handle)))

      const fetched = await fetchColourSwatchesByHandles(
        normalizedMissingColours
      )

      const merged = [...cachedSwatches]
      for (const swatch of fetched) {
        const handle = swatch?.colour_handle

        if (handle && normalizedMissingColours.includes(handle)) {
          merged.push(swatch)
        }
      }

      sessionStorage.setItem(COLOUR_SWATCH_KEY, JSON.stringify(merged))
      setSwatchColours(merged)
    }

    syncSwatchCache()
  }, [results])

  return swatchColours
}
